import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ApartmentOutlined,
  AutoAwesomeOutlined,
  CloseOutlined,
  DashboardOutlined,
  ExpandMoreOutlined,
  HubOutlined,
  LogoutOutlined,
  MemoryOutlined,
  MenuOutlined,
  MonitorHeartOutlined,
  ReceiptLongOutlined,
  RefreshOutlined,
  SettingsOutlined,
  TravelExploreOutlined,
  TuneOutlined,
  VerifiedUserOutlined, PendingActionsOutlined, AccountCircleOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { platformAuthApi } from '@/api/platform.api';
import { useNotify } from '@/components/common/useNotify';
import { storage } from '@/lib/storage';
import { PLATFORM_LOGIN_PATH, platformPath } from '@/config/appTarget';
import { hasPlatformAccess } from '@/auth/platformAccess';
import PlatformLoginPage from './PlatformLoginPage';
import { ApiError } from '@/lib/apiClient';
import { queryClient } from '@/lib/queryClient';
import { PLATFORM_SETTINGS_PATHS } from './PlatformSettingsPage';

const BAR_BG = '#1e1b4b';

/**
 * Platform Console shell. Auth via the platform plane (separate from tenant auth):
 *   GET /api/platform/auth/profile · POST /refresh-token · POST /logout.
 *
 * Nothing inside renders until the server has confirmed a platform user with a platform role
 * (auth/platformAccess). A token the API refuses, or a profile without such a role, ends the session
 * and returns to sign-in. The API enforces the same on every call; this keeps the screens honest.
 */
export default function PlatformLayout() {
  const navigate = useNavigate();
  const notify = useNotify();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Every hook stays above the early returns below (hook order must not change between renders).
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const hasToken = !!storage.accessToken;
  const profileQ = useQuery({ queryKey: ['platform-profile'], queryFn: platformAuthApi.profile, retry: false, enabled: hasToken });

  // Refused by the server, or signed in without a platform role.
  const refused = profileQ.error instanceof ApiError && (profileQ.error.status === 401 || profileQ.error.status === 403);
  const denied = refused || (!!profileQ.data && !hasPlatformAccess(profileQ.data));

  useEffect(() => {
    if (!denied) return;
    storage.clearSession();
    queryClient.clear();
  }, [denied]);

  // The mobile menu closes itself once a destination is picked.
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  if (denied) return <Navigate to={PLATFORM_LOGIN_PATH} replace state={{ denied: true }} />;
  if (!hasToken) {
    // madmin.tripza.in: the bare address is the sign-in page, so it shows here rather than redirecting.
    if (pathname === PLATFORM_LOGIN_PATH) return <PlatformLoginPage />;
    return <Navigate to={PLATFORM_LOGIN_PATH} replace />;
  }

  // The console waits for the server's word on who this is before any screen asks it for data.
  if (profileQ.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Stack alignItems="center" spacing={1.25}>
          <CircularProgress size={32} />
          <Typography variant="caption" color="text.secondary">Opening Platform Console…</Typography>
        </Stack>
      </Box>
    );
  }

  const refresh = async () => {
    const token = storage.refreshToken;
    if (!token) return;
    try {
      const res = await platformAuthApi.refreshToken(token);
      storage.setSession(res);
      notify.success('Session refreshed.');
    } catch (e) {
      notify.error(e);
    }
  };

  const logout = async () => {
    const token = storage.refreshToken;
    try { if (token) await platformAuthApi.logout(token); } catch { /* best effort */ }
    storage.clearSession();
    queryClient.clear(); // the next person to sign in must not see this one's cached data
    navigate(PLATFORM_LOGIN_PATH, { replace: true });
  };

  const fullName = profileQ.data?.fullName;

  return (
    // overflowX 'clip' (not 'hidden'): hidden turns this box into a scroll container and the sticky top bar stops sticking.
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', overflowX: 'clip' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: BAR_BG }}>
        <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 60 }, px: { xs: 1, sm: 2 } }}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <MenuOutlined />
          </IconButton>
          <Typography variant="h6" fontWeight={800} noWrap sx={{ mr: { md: 2 }, fontSize: { xs: '1rem', sm: '1.125rem' }, flexShrink: 0 }}>
            Platform Console
          </Typography>

          {/* Desktop nav: one row, scrolls inside itself rather than pushing the page wider. */}
          <Stack
            direction="row"
            spacing={0.25}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexGrow: 1,
              minWidth: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {PRIMARY_ITEMS.slice(0, AI_AFTER).map((item) => (
              <PlatformNavLink key={item.to} {...item} />
            ))}
            <PlatformAiManagementNav />
            {PRIMARY_ITEMS.slice(AI_AFTER).map((item) => (
              <PlatformNavLink key={item.to} {...item} />
            ))}
          </Stack>
          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          {profileQ.data ? (
            // One account button (who is signed in, Refresh session, Logout), so every menu item fits on the bar.
            <>
              <Tooltip title={fullName ?? 'Account'}>
                <IconButton color="inherit" aria-label="Account" onClick={(e) => setAccountAnchor(e.currentTarget)} sx={{ flexShrink: 0 }}>
                  <AccountCircleOutlined />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700}>{fullName}</Typography>
                  <Typography variant="caption" color="text.secondary">Platform Console</Typography>
                </Box>
                <Divider />
                <MenuItem dense onClick={() => { setAccountAnchor(null); refresh(); }}>
                  <ListItemIcon><RefreshOutlined fontSize="small" /></ListItemIcon><ListItemText>Refresh session</ListItemText>
                </MenuItem>
                <MenuItem dense onClick={() => { setAccountAnchor(null); logout(); }}>
                  <ListItemIcon><LogoutOutlined fontSize="small" /></ListItemIcon><ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button color="inherit" size="small" startIcon={<LogoutOutlined />} onClick={logout} sx={{ flexShrink: 0 }}>Sign in again</Button>
          )}
        </Toolbar>
      </AppBar>

      <PlatformNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} fullName={fullName} onLogout={logout} />

      {/* Wide screens use the width: at most 1600px, with a small gutter. */}
      <Container maxWidth={false} sx={{ maxWidth: 1600, py: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}>
        <Outlet />
      </Container>
    </Box>
  );
}

type NavItem = { to: string; label: string; icon: ReactNode; alsoActiveOn?: string[] };

/**
 * Top-level destinations, in this order: Dashboard | Companies | Pending Onboarding | KYC Reviews | AI Management |
 * Destination Library | Subscriptions | Settings. AI Management (with AI Ops) is the dropdown after the first AI_AFTER.
 */
const PRIMARY_ITEMS: NavItem[] = [
  { to: platformPath('/dashboard'), label: 'Dashboard', icon: <DashboardOutlined fontSize="small" /> },
  { to: platformPath('/tenants'), label: 'Companies', icon: <ApartmentOutlined fontSize="small" /> },
  { to: platformPath('/onboarding/pending'), label: 'Pending Onboarding', icon: <PendingActionsOutlined fontSize="small" /> },
  { to: platformPath('/kyc'), label: 'KYC Reviews', icon: <VerifiedUserOutlined fontSize="small" /> },
  { to: platformPath('/library'), label: 'Destination Library', icon: <TravelExploreOutlined fontSize="small" /> },
  { to: platformPath('/subscriptions'), label: 'Subscriptions', icon: <ReceiptLongOutlined fontSize="small" /> },
  // GST Lookup and Setup Options are reached from Settings.
  { to: platformPath('/settings'), label: 'Settings', icon: <SettingsOutlined fontSize="small" />, alsoActiveOn: PLATFORM_SETTINGS_PATHS },
];

/** How many PRIMARY_ITEMS come before the AI Management dropdown. */
const AI_AFTER = 4;

/** The AI Management destinations. Kept beside the nav so adding a page is a one-line change. */
const AI_MANAGEMENT_ITEMS = [
  { to: platformPath('/ai-ops'), label: 'AI Ops', icon: <MonitorHeartOutlined fontSize="small" /> },
  { to: platformPath('/ai/providers'), label: 'Provider Catalog', icon: <HubOutlined fontSize="small" /> },
  { to: platformPath('/ai/configuration'), label: 'Platform AI Configuration', icon: <TuneOutlined fontSize="small" /> },
  { to: platformPath('/ai/models'), label: 'Models', icon: <MemoryOutlined fontSize="small" /> },
];

function isLit(pathname: string, item: NavItem) {
  return pathname === item.to
    || pathname.startsWith(`${item.to}/`)
    || (item.alsoActiveOn?.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false);
}

/** Mobile / tablet navigation: every destination in one list, plus who is signed in. */
function PlatformNavDrawer({ open, onClose, fullName, onLogout }: {
  open: boolean; onClose: () => void; fullName?: string; onLogout: () => void;
}) {
  const { pathname } = useLocation();
  const link = (item: NavItem) => (
    <ListItemButton
      key={item.to}
      component={NavLink}
      to={item.to}
      selected={isLit(pathname, item)}
      onClick={onClose}
      sx={{ borderRadius: 1.5, mx: 1, py: 0.75 }}
    >
      <ListItemIcon sx={{ minWidth: 34 }}>{item.icon}</ListItemIcon>
      <ListItemText primary={item.label} primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
    </ListItemButton>
  );

  return (
    <Drawer anchor="left" open={open} onClose={onClose} sx={{ display: { md: 'none' } }} PaperProps={{ sx: { width: 'min(300px, 85vw)' } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5, bgcolor: BAR_BG, color: '#fff' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>Platform Console</Typography>
          {fullName && <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap component="div">{fullName}</Typography>}
        </Box>
        <IconButton color="inherit" size="small" aria-label="Close navigation" onClick={onClose}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Stack>
      <List dense sx={{ py: 1 }}>
        {PRIMARY_ITEMS.slice(0, AI_AFTER).map(link)}
      </List>
      <List dense sx={{ py: 0 }} subheader={<ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>AI Management</ListSubheader>}>
        {AI_MANAGEMENT_ITEMS.map(link)}
      </List>
      <List dense sx={{ py: 1 }}>
        {PRIMARY_ITEMS.slice(AI_AFTER).map(link)}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider />
      <List dense sx={{ py: 1 }}>
        <ListItemButton onClick={onLogout} sx={{ borderRadius: 1.5, mx: 1, py: 0.75 }}>
          <ListItemIcon sx={{ minWidth: 34 }}><LogoutOutlined fontSize="small" /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }} />
        </ListItemButton>
      </List>
    </Drawer>
  );
}

const navButtonSx = (lit: boolean) => ({
  flexShrink: 0,
  px: 1.25,
  whiteSpace: 'nowrap',
  color: lit ? '#fff' : 'rgba(255,255,255,0.75)',
  bgcolor: lit ? 'rgba(255,255,255,0.14)' : 'transparent',
  '&.active': { color: '#fff', bgcolor: 'rgba(255,255,255,0.14)' },
  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
  '& .MuiButton-startIcon': { mr: 0.5 },
});

/**
 * AI Management section. A dropdown rather than three more top-level buttons: the console's top bar is a
 * single row, and three extra entries would crowd out Dashboard / Tenants / AI Ops.
 */
function PlatformAiManagementNav() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { pathname } = useLocation();
  // Highlighted whenever any child route is open, matching how the NavLink buttons behave.
  const active = pathname.startsWith(platformPath('/ai/')) || pathname.startsWith(platformPath('/ai-ops'));

  return (
    <>
      <Button
        color="inherit"
        size="small"
        startIcon={<AutoAwesomeOutlined fontSize="small" />}
        endIcon={<ExpandMoreOutlined fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        sx={{ ...navButtonSx(active), '& .MuiButton-endIcon': { ml: 0.25 } }}
      >
        AI Management
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {AI_MANAGEMENT_ITEMS.map((item) => (
          <MenuItem
            key={item.to}
            component={NavLink}
            to={item.to}
            selected={pathname === item.to}
            onClick={() => setAnchor(null)}
            dense
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Top-bar nav link with active highlight. New Super Admin sections plug in here as they ship. */
function PlatformNavLink({ to, icon, label, alsoActiveOn }: NavItem) {
  const { pathname } = useLocation();
  // A section whose pages live at their own paths (Settings) stays lit on each of them.
  const lit = alsoActiveOn?.some((p) => pathname === p || pathname.startsWith(`${p}/`)) ?? false;
  return (
    <Button component={NavLink} to={to} color="inherit" size="small" startIcon={icon} sx={navButtonSx(lit)}>
      {label}
    </Button>
  );
}
